import React, { useEffect } from 'react';
import '../styles/BillingPage.css';
import googleReviewQr from '../assets/google-review-qr.webp';
import logoFull from '../assets/logo_full.webp';

const InvoicePreview = ({ bill, onClose, onPrint, autoPrint }) => {
    useEffect(() => {
        if (bill) {
            const dateStr = new Date(bill.createdAt || bill.date || new Date()).toLocaleDateString('en-IN').replace(/\//g, '-');
            const name = bill.customerName || (bill.customer ? `${bill.customer.firstName} ${bill.customer.lastName}` : 'Guest');
            document.title = `${name}_${dateStr}`;
        }

        // Restore title on unmount
        return () => {
            document.title = 'MAB Jewels (Manoj Abhushan Bhandar, Rajasthan) - Invoice';
        };
    }, [bill]);

    useEffect(() => {
        if (autoPrint) {
            // Small timeout to ensure DOM is ready
            setTimeout(() => window.print(), 500);
        }
    }, [autoPrint]);

    if (!bill) return null;

    // --- Helper to normalize data structure ---
    const billData = {
        billNumber: bill.billNumber || 'DRAFT',
        date: bill.createdAt || bill.date || new Date(), // Handle both
        customerName: bill.customerName || (bill.customer ? `${bill.customer.firstName} ${bill.customer.lastName}` : 'Guest'),
        customerPhone: bill.customerPhone || bill.customer?.phone || '',
        customerCity: bill.customer?.address?.city || '',
        items: bill.items || [],
        subtotal: bill.subtotal || 0,
        gst: bill.gst || 0,
        discount: bill.discount || 0,
        grandTotal: bill.totalAmount || bill.grandTotal || 0,
        paymentMode: bill.paymentDetails?.mode || bill.paymentMethod || 'Cash'
    };

    const cgst = bill.taxBreakup?.cgst || (billData.gst / 2);
    const sgst = bill.taxBreakup?.sgst || (billData.gst - cgst);

    return (
        <div className="invoice-preview-modern">
            {/* Header Section */}
            <div className="invoice-header-row">
                <div className="invoice-brand">
                    <img src={logoFull} alt="MAB Jewels" className="invoice-logo" />
                    <div className="brand-tagline">Premium Gold, Silver & Diamond Jewellery</div>
                    <div className="brand-address">
                        MAB Jewels (Manoj Abhushan Bhandar, Rajasthan)<br />
                        +91 9414817620 | mabjewels.store@gmail.com
                    </div>
                </div>
                <div className="invoice-meta">
                    <h1 className="invoice-title">TAX INVOICE</h1>
                    <div className="meta-grid">
                        <div className="meta-item">
                            <span className="meta-label">Invoice No:</span>
                            <span className="meta-value">{billData.billNumber}</span>
                        </div>
                        <div className="meta-item">
                            <span className="meta-label">Date:</span>
                            <span className="meta-value">{new Date(billData.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        </div>
                        {billData.gst > 0 && (
                            <div className="meta-item">
                                <span className="meta-label">GSTIN:</span>
                                <span className="meta-value">08BPIPS2564F1ZM</span>
                            </div>
                        )}
                        <div className="meta-item">
                            <span className="meta-label">Payment:</span>
                            <span className="meta-value">{billData.paymentMode}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Billed To */}
            <div className="invoice-billed-to">
                <span className="billed-label">Billed To</span>
                <div className="customer-details">
                    <span className="customer-name">{billData.customerName}</span>
                    <span className="customer-info">{billData.customerPhone} {billData.customerCity ? `• ${billData.customerCity}` : ''}</span>
                </div>
            </div>

            {/* Table */}
            <table className="invoice-table">
                <thead>
                    <tr>
                        <th style={{ width: '5%', textAlign: 'center' }}>#</th>
                        <th style={{ width: '35%', textAlign: 'left' }}>Description</th>
                        <th style={{ width: '10%', textAlign: 'center' }}>HUID</th>
                        <th style={{ width: '10%', textAlign: 'right' }}>Rate (₹)</th>
                        <th style={{ width: '12%', textAlign: 'right' }}>Weight</th>
                        <th style={{ width: '13%', textAlign: 'right' }}>Making (₹)</th>
                        <th style={{ width: '15%', textAlign: 'right' }}>Amount (₹)</th>
                    </tr>
                </thead>
                <tbody>
                    {billData.items.map((item, i) => (
                        <tr key={i}>
                            <td style={{ textAlign: 'center' }}>{i + 1}</td>
                            <td style={{ textAlign: 'left' }}>
                                <div className="item-name">{item.name}</div>
                                <div className="item-sub">{item.metalType} | {item.purity}</div>
                            </td>
                            <td className="mono-font" style={{ textAlign: 'center' }}>{item.huid || '-'}</td>
                            <td style={{ textAlign: 'right' }}>{Number(item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td style={{ textAlign: 'right' }}>
                                <div>{item.netWeight}g</div>
                                {Number(item.grossWeight) > Number(item.netWeight) && <div className="item-sub">Gr: {item.grossWeight}g</div>}
                            </td>
                            <td style={{ textAlign: 'right' }}>{Number(item.makingCharges).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{(item.total || item.lineTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Footer & Terms */}
            <div className="invoice-footer-section">
                <div className="terms-box">
                    <strong>Terms & Conditions</strong>
                    <ul>
                        <li>All purchases are covered under MAB Jewels exchange policy.</li>
                        <li>Certificated Jewellery with Lifetime Exchange.</li>
                        <li>Subject to Jodhpur Jurisdiction.</li>
                    </ul>
                    <div style={{ marginTop: '5px', fontSize: '10px', fontWeight: 'bold' }}>* Terms & Conditions Applied</div>
                </div>

                <div className="totals-box">
                    <div className="total-row dim">
                        <span>Subtotal</span>
                        <span>₹{Number(billData.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    {/* Tax Rows Only if GST > 0 */}
                    {billData.gst > 0 && (
                        <>
                            <div className="total-row dim">
                                <span>Add: CGST (1.5%)</span>
                                <span>₹{(billData.gst / 2).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="total-row dim">
                                <span>Add: SGST (1.5%)</span>
                                <span>₹{(billData.gst / 2).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                        </>
                    )}

                    <div className="grand-total-row">
                        <span>Grand Total</span>
                        <span>₹{Number(bill.totalAmount || bill.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>

                    <div className="amount-paid-row">
                        <span>Amount Paid</span>
                        <span>₹{Number(bill.paymentDetails?.advance || bill.paidAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                </div>
            </div>

            {/* Bottom Bar: Signatures & QR */}
            <div className="invoice-bottom-bar">
                <div className="sign-box left">
                    <div className="sign-line"></div>
                    <span>Customer Signature</span>
                </div>

                <div className="qr-section">
                    <img src={googleReviewQr} alt="Review us" className="qr-img" />
                    <span className="qr-text">Review us on Google</span>
                </div>

                <div className="sign-box right">
                    <div className="sign-line"></div>
                    <span>Authorized Signatory</span>
                </div>
            </div>

            <div className="computer-generated-note">
                BIS Hallmarked Jewellery | Certified Purity<br />
                Thank you for choosing MAB Jewels (Manoj Abhushan Bhandar, Rajasthan)<br />
                <span style={{ fontSize: '8px', color: '#ccc', marginTop: '4px', display: 'block' }}>This is a computer-generated invoice</span>
            </div>

            {/* Print Buttons */}
            {!autoPrint && (
                <div className="print-actions-floating">
                    <button className="btn-close" onClick={onClose}>Close</button>
                    <button className="btn-print" onClick={onPrint || (() => window.print())}>🖨️ Print / Save PDF</button>
                </div>
            )}
        </div>
    );
};
export default InvoicePreview;
